/**
 * Order Lookup Page Route
 * Allows customers to look up orders by email and order number
 * Uses TanStack Form for type-safe form handling
 */

import { useEffect } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { Package, Mail, Hash, Loader2, AlertCircle, Clock } from 'lucide-react';
import { verifyOrderLookup } from '@/lib/api-client';
import {
  trackOrderLookupAttempt,
  trackOrderLookupFailed,
} from '@/lib/analytics-client';

const supportEmail = 'hello@fathomcoffee.com';

function buildLookupSupportHref(orderNumber?: string) {
  const subject = encodeURIComponent(
    orderNumber ? `Order lookup help for #${orderNumber}` : 'Order lookup help'
  );
  const body = encodeURIComponent(
    orderNumber
      ? `Hi Fathom Coffee,\n\nI need help finding order #${orderNumber}.\n\nThanks!`
      : 'Hi Fathom Coffee,\n\nI need help with order lookup.\n\nThanks!'
  );
  return `mailto:${supportEmail}?subject=${subject}&body=${body}`;
}

function getLookupErrorMessage(error: unknown) {
  if (!(error instanceof Error)) {
    return 'We could not verify your order right now. Please try again in a moment, or contact us if it keeps happening.';
  }

  const message = error.message.toLowerCase();

  if (
    message.includes('too many attempts') ||
    message.includes('too many requests') ||
    message.includes('rate limit') ||
    message.includes('429')
  ) {
    return 'Too many lookup attempts were made from this connection. Please wait a minute and try again.';
  }

  if (message.includes('order not found')) {
    return 'We could not find that order. Double-check the email address and order number from your confirmation email.';
  }

  return 'We could not verify your order right now. Please try again in a moment, or contact us if it keeps happening.';
}

const lookupSearchSchema = z.object({
  expired: z.boolean().optional(),
});

export const Route = createFileRoute('/lookup')({
  validateSearch: lookupSearchSchema,
  component: LookupPage,
});

// Form validation schema
const lookupFormSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(254, 'Email is too long'),
  orderNumber: z
    .string()
    .min(1, 'Order number is required')
    .max(20, 'Order number is too long')
    .regex(/^[0-9]+$/, 'Order number should contain only numbers'),
});

type LookupFormData = z.infer<typeof lookupFormSchema>;

function LookupPage() {
  const navigate = useNavigate();
  const { expired } = Route.useSearch();

  useEffect(() => {
    document.title = 'Find Your Order - Fathom Coffee';
  }, []);

  // Mutation for order verification
  const verifyMutation = useMutation({
    mutationFn: async (data: LookupFormData) => {
      trackOrderLookupAttempt();
      const result = await verifyOrderLookup(
        data.email.trim().toLowerCase(),
        data.orderNumber.trim()
      );
      return result;
    },
    onSuccess: (data) => {
      // Redirect to results page - session stored in httpOnly cookie
      navigate({
        to: '/lookup/results',
        search: { order: data.orderNumber },
      });
    },
    onError: (error) => {
      trackOrderLookupFailed(error instanceof Error ? error.message : 'unknown_error');
    },
  });

  // TanStack Form setup with native Zod support via Standard Schema
  const form = useForm({
    defaultValues: {
      email: '',
      orderNumber: '',
    } as LookupFormData,
    onSubmit: async ({ value }) => {
      await verifyMutation.mutateAsync(value);
    },
    validators: {
      onChange: lookupFormSchema,
    },
  });
  const supportHref = buildLookupSupportHref((form.state.values.orderNumber ?? '').trim() || undefined);

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-heading text-foreground mb-3 tracking-tight">Find Your Order</h1>
        <p className="text-foreground-muted">
          Use the email address from checkout and the numeric order number from your confirmation
          email to view your shipment details.
        </p>
      </div>

      {expired && (
        <div
          role="status"
          className="mb-6 flex items-start gap-3 p-4 bg-fathom-gold/10 border border-fathom-gold/30 rounded-lg"
        >
          <Clock className="w-5 h-5 text-fathom-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium text-foreground">Session expired</p>
            <p className="text-sm text-foreground-muted">
              Your order session expired after a period of inactivity. Please re-enter your email and
              order number to view your shipments again.
            </p>
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg shadow-lg p-8 border border-border theme-transition">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="space-y-6"
          aria-busy={verifyMutation.isPending}
        >
          <p className="text-sm text-foreground-muted">
            We use these details to verify your order before showing shipment information.
          </p>

          {/* Email Input */}
          <form.Field name="email">
            {(field) => (
              (() => {
                const hasError = field.state.meta.errors.length > 0 && field.state.meta.isTouched;
                const descriptionId = `${field.name}-description`;
                const errorId = `${field.name}-error`;
                const describedBy = hasError
                  ? `${descriptionId} ${errorId}`
                  : descriptionId;

                return (
                  <div>
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  <Mail className="w-4 h-4 inline mr-2" aria-hidden="true" />
                  Email Address
                </label>
                <input
                  type="email"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-fathom-gold focus:border-fathom-gold outline-none transition-colors bg-background text-foreground ${
                    hasError ? 'border-red-300' : 'border-border'
                  }`}
                  aria-invalid={hasError}
                  aria-describedby={describedBy}
                />
                <p id={descriptionId} className="mt-1 text-xs text-foreground-muted">
                  The email address used when placing your order
                </p>
                {hasError && (
                  <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
                    {field.state.meta.errors[0]?.message}
                  </p>
                )}
                  </div>
                );
              })()
            )}
          </form.Field>

          {/* Order Number Input */}
          <form.Field name="orderNumber">
            {(field) => (
              (() => {
                const hasError = field.state.meta.errors.length > 0 && field.state.meta.isTouched;
                const descriptionId = `${field.name}-description`;
                const errorId = `${field.name}-error`;
                const describedBy = hasError
                  ? `${descriptionId} ${errorId}`
                  : descriptionId;

                return (
                  <div>
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  <Hash className="w-4 h-4 inline mr-2" aria-hidden="true" />
                  Order Number
                </label>
                <input
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  inputMode="numeric"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="e.g., 10457"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-fathom-gold focus:border-fathom-gold outline-none transition-colors font-mono bg-background text-foreground ${
                    hasError ? 'border-red-300' : 'border-border'
                  }`}
                  aria-invalid={hasError}
                  aria-describedby={describedBy}
                />
                <p id={descriptionId} className="mt-1 text-xs text-foreground-muted">
                  Found in your order confirmation email. Use numbers only.
                </p>
                {hasError && (
                  <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
                    {field.state.meta.errors[0]?.message}
                  </p>
                )}
                  </div>
                );
              })()
            )}
          </form.Field>

          {/* Error Message */}
          {verifyMutation.error && (
            <div
              role="alert"
              className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-red-700 dark:text-red-400">
                {getLookupErrorMessage(verifyMutation.error)}
              </p>
            </div>
          )}

          <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {verifyMutation.isPending ? 'Looking up your order.' : null}
          </div>

          {/* Submit Button */}
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
            {([canSubmit, isSubmitting]) => (
              <button
                type="submit"
                disabled={!canSubmit || isSubmitting || verifyMutation.isPending}
                className="w-full px-6 py-3 bg-fathom-gold text-white font-medium rounded-lg hover:bg-fathom-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting || verifyMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Looking up...
                  </>
                ) : (
                  'Find My Order'
                )}
              </button>
            )}
          </form.Subscribe>
        </form>

        {/* Back Link */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-foreground-muted hover:text-foreground transition-colors">
            ← Back to tracking number entry
          </Link>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-8 text-center text-sm text-foreground-muted">
        <p>
          Need help? Contact us at{' '}
          <a href={supportHref} className="text-fathom-blue hover:underline">
            {supportEmail}
          </a>
          . Include your order number and the email address used at checkout.
        </p>
      </div>
    </div>
  );
}
